import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { createAppStore, getPersistor, ApiUrls } from '@ideoz/core';
import { default as apiClient } from '@ideoz/core/api/client';
import { initAnalytics } from '@ideoz/core/analytics';

import App from './App';
import './index.scss';
import mixpanel from "mixpanel-browser";
import { FlowsProvider } from '@flows/react';
import { components, tourComponents } from './flows-components';
import { Link } from './components';
import { useSelector } from 'react-redux';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string;

try {
  mixpanel.init(MIXPANEL_TOKEN, {
    autocapture: true,
    track_pageview: true,
    record_sessions_percent: 100,
    record_heatmap_data: true,
    record_mask_all_text: false,
    ignore_dnt: true,
  });
} catch (e) {
  console.warn('Mixpanel initialization failed:', e);
}

// Production-only: Google Analytics (gtag.js), Google Tag Manager, and Meta Pixel
if (import.meta.env.MODE === 'production') {
  const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string;
  const GTM_ID = import.meta.env.VITE_GTM_ID as string;
  const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string;

  try {
    // Google tag (gtag.js)
    if (GA_ID) {
      const gtagScript = document.createElement('script');
      gtagScript.async = true;
      gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      document.head.appendChild(gtagScript);

      const gtagInit = document.createElement('script');
      gtagInit.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${GA_ID}', { send_page_view: false });
      `;
      document.head.appendChild(gtagInit);
    }

    // Google Tag Manager
    if (GTM_ID) {
      const gtmScript = document.createElement('script');
      gtmScript.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];
        w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
        var f=d.getElementsByTagName(s)[0],j=d.createElement(s),
        dl=l!='dataLayer'?'&l='+l:'';
        j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
        f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${GTM_ID}');`;
      document.head.appendChild(gtmScript);

      // GTM noscript fallback
      const gtmNoscript = document.createElement('noscript');
      gtmNoscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.insertBefore(gtmNoscript, document.body.firstChild);
    }

    // Meta Pixel Code
    if (META_PIXEL_ID) {
      const metaPixel = document.createElement('script');
      metaPixel.textContent = `!function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window,document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init','${META_PIXEL_ID}');
        fbq('track','PageView');`;
      document.head.appendChild(metaPixel);

      // Meta Pixel noscript fallback
      const metaNoscript = document.createElement('noscript');
      metaNoscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1" />`;
      document.body.appendChild(metaNoscript);
    }
  } catch (e) {
    console.warn('Production analytics tags initialization failed:', e);
  }
}

// Create Redux store
const store = createAppStore();
const persistor = getPersistor(store);

// Initialize analytics service
initAnalytics(apiClient, ApiUrls.events, () => store.getState() as any);

// Sync logged-in user identity with Mixpanel session replay, skip for @sieraj.com emails
let previousIsLoggedIn: boolean | null = null;
store.subscribe(() => {
  try {
    const state = store.getState() as any;
    const isLoggedIn: boolean = state.app?.isLoggedIn ?? false;
    const user = state.app?.user;
    const isSierajUser = user?.email?.includes('@sieraj.com');

    if (isLoggedIn === previousIsLoggedIn) return;
    previousIsLoggedIn = isLoggedIn;

    if (isSierajUser) return;

    if (isLoggedIn && user?.id) {
      mixpanel.identify(String(user.id));
      mixpanel.people.set({
        $user_id: user.id,
        $name: user.name ?? '',
        $email: user.email ?? '',
        $avatar: user.avatar ?? '',
        provider: user.provider ?? '',
      });
    } else if (!isLoggedIn) {
      mixpanel.reset();
    }
  } catch (e) {
    // Non-blocking — never let this break the app
  }
});

// Wrapper to provide live user data to FlowsProvider, with exclusion for @sieraj.com emails
function FlowsProviderWrapper({ children }: { children: React.ReactNode }) {
  const user = useSelector((state: any) => state.app?.user);
  const isSierajUser = user?.email?.includes('@sieraj.com');
  if (isSierajUser) {
    return <>{children}</>;
  }
  return (
    <FlowsProvider
      organizationId="4a95b56e-0f12-41bb-bd1d-c23add965dc3"
      userId={user?.id || ''}
      userProperties={{
        email: user?.email || '',
        name: user?.name || '',
      }}
      environment="production"
      components={{ ...components }}
      tourComponents={{ ...tourComponents }}
      LinkComponent={Link}
    >
      {children}
    </FlowsProvider>
  );
}

const rootElement = document.getElementById('root');

if (rootElement) {
  if (!(rootElement as any)._reactRoot) {
    (rootElement as any)._reactRoot = ReactDOM.createRoot(rootElement);
  }
  (rootElement as any)._reactRoot.render(
    <React.StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          {import.meta.env.MODE === 'production' ? (
            <FlowsProviderWrapper>
              <App />
            </FlowsProviderWrapper>
          ) : (
            <App />
          )}
        </PersistGate>
      </Provider>
    </React.StrictMode>
  );
}
